import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable, map, startWith } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import { ClientRequestDetails } from '../../models/api-request-response-info.model';
import { ApiRequestResponseInfoService } from '../../services/api-request-response-info.service';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { toggleAutocompletePanel } from '../../shared/utils/autocomplete-panel.utils';

@Component({
  selector: 'app-apirequestresponseinfo',
  standalone: false,
  templateUrl: './api-request-response-info.component.html',
  styleUrl: './api-request-response-info.component.css',
})
export class ApirequestresponseinfoComponent extends BaseTableComponent<ClientRequestDetails> {
  override sortBy = 'requestId';
  override sortDir: 'asc' | 'desc' = 'asc';
  private hasAppliedFilter = false;

  readonly displayedColumns: string[] = [
    'claimKey',
    'apiMethod',
    'requestDate',
    'requestedBy',
    'responseDate',
    'RequestFile',
    'ResponseFile',
    'responseTime',
    'status',
  ];

  readonly apiMethods: string[] = [
    'GetClaimStatus',
    'GetBeneficiaryEnrollment',
    'SubmitClaim',
    'SubmitClaims',
    'DeleteClaims',
    'GetAlerts',
    'AcknowledgeAlerts',
    'SubmitOrgs',
    'GetUnsolicitedResponses',
  ].sort((a, b) => a.localeCompare(b));

  readonly statuses: string[] = ['Error', 'Failed', 'Success'];

  statusControl = new FormControl<string>('', { nonNullable: true });

  filteredStatuses$: Observable<string[]> =
    this.statusControl.valueChanges.pipe(
      startWith(this.statusControl.value),
      map((value) => this.filterOptions(this.statuses, value)),
    );

  filter = {
    claimKey: '',
    apiMethod: 'SubmitClaim',
    status: '',
    requestDate: null as Date | null,
    requestedBy: '',
  };

  constructor(
    private apiRequestResponseInfoService: ApiRequestResponseInfoService,
  ) {
    super();
  }

  protected override loadData(skip: number, count: number): void {
    if (!this.hasAppliedFilter) {
      this.dataSource.data = [];
      this.totalCount = 0;
      this.errorMessage = '';
      return;
    }

    this.loading = true;

    this.apiRequestResponseInfoService
      .getClientRequestDetails(
        skip,
        count,
        this.sortBy,
        this.sortDir,
        this.filter,
      )
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.clientRequests;
          this.totalCount = response.totalCount;
          this.errorMessage = '';
        },
        error: (err) => {
          this.errorMessage = formatHttpErrorMessage(
            'Failed to load API request details',
            err,
          );
          this.dataSource.data = [];
          this.totalCount = 0;
        },
      });
  }

  onFilter(): void {
    const selectedStatus = this.normalizeSelection(
      this.statuses,
      this.statusControl.value,
    );

    this.filter.status = selectedStatus;

    this.statusControl.setValue(selectedStatus, { emitEvent: false });

    this.hasAppliedFilter = true;
    this.pageIndex = 0;
    this.loadDataWrapper();
  }

  clearSelectedStatus(event: Event): void {
    event.stopPropagation();
    this.statusControl.setValue('');
    this.filter.status = '';
  }

  openOrClosePanel(event: Event, trigger: MatAutocompleteTrigger): void {
    toggleAutocompletePanel(event, trigger);
  }

  displayOption(value: string | null): string {
    return value ?? '';
  }

  private filterOptions(options: string[], value: string): string[] {
    const normalizedValue = value.trim().toLowerCase();
    return options.filter((option) =>
      option.toLowerCase().includes(normalizedValue),
    );
  }

  private normalizeSelection(options: string[], value: string): string {
    const normalizedValue = value.trim().toLowerCase();
    const matched = options.find(
      (option) => option.toLowerCase() === normalizedValue,
    );
    return matched ?? '';
  }
}
